import { useEffect, useRef } from 'react';

/** Presentation-only position; never changes hero or camera coordinates. */
export function useDraggableJournal(shown: boolean, anchor: 'left' | 'right') {
  const panelRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const position = useRef<{ x: number; y: number } | null>(null);
  useEffect(() => {
    const panel = panelRef.current, header = headerRef.current;
    const viewport = panel?.offsetParent as HTMLElement | null;
    if (!shown || !panel || !header || !viewport) return;
    let drag: { id: number; x: number; y: number; startX: number; startY: number } | null = null;
    const place = (x: number, y: number) => {
      const point = {
        x: Math.max(0, Math.min(x, viewport.clientWidth - panel.offsetWidth)),
        y: Math.max(0, Math.min(y, viewport.clientHeight - panel.offsetHeight)),
      };
      position.current = point;
      panel.style.left = `${point.x}px`;
      panel.style.top = `${point.y}px`;
      panel.style.right = 'auto';
      panel.style.setProperty('bottom', 'auto', 'important');
    };
    const down = (event: PointerEvent) => {
      if (!event.isPrimary || event.button !== 0 || (event.target as Element).closest('button')) return;
      drag = { id: event.pointerId, x: event.clientX, y: event.clientY, startX: panel.offsetLeft, startY: panel.offsetTop };
      header.setPointerCapture(event.pointerId);
      event.preventDefault();
    };
    const move = (event: PointerEvent) => {
      if (!drag || drag.id !== event.pointerId) return;
      place(drag.startX + event.clientX - drag.x, drag.startY + event.clientY - drag.y);
    };
    const end = (event: PointerEvent) => {
      if (drag?.id !== event.pointerId) return;
      drag = null;
      if (header.hasPointerCapture(event.pointerId)) header.releasePointerCapture(event.pointerId);
    };
    const clamp = () => { if (position.current) place(position.current.x, position.current.y); };
    if (position.current) clamp();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(clamp) : null;
    observer?.observe(viewport);
    observer?.observe(panel);
    window.addEventListener('resize', clamp);
    header.addEventListener('pointerdown', down);
    header.addEventListener('pointermove', move);
    header.addEventListener('pointerup', end);
    header.addEventListener('pointercancel', end);
    header.addEventListener('lostpointercapture', end);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', clamp);
      header.removeEventListener('pointerdown', down);
      header.removeEventListener('pointermove', move);
      header.removeEventListener('pointerup', end);
      header.removeEventListener('pointercancel', end);
      header.removeEventListener('lostpointercapture', end);
      if (drag && header.hasPointerCapture(drag.id)) header.releasePointerCapture(drag.id);
    };
  }, [shown, anchor]);
  return { panelRef, headerRef };
}
